import ExcelJS from "exceljs";
import * as XLSX from "xlsx";

const TEMPLATE_HEADERS = [
  "DATE",
  "Debit Ledger name",
  "Credit Ledger name",
  "Narration",
  "Amount",
  "Voucher Type",
];

const VOUCHER_TYPES = ["Journal", "Payment", "Receipt", "Contra"];

const triggerDownload = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
};

/**
 * Generate the bulk-voucher template:
 *  - 6 columns: Date / Dr / Cr / Narration / Amount / Voucher Type
 *  - Row 1 locked headers
 *  - Sheet protected (only A-F editable from row 2)
 *  - Data validations: dd/mm/yyyy date, ledger dropdowns, decimal amount, voucher-type dropdown
 *  - 2 lightly-styled sample rows the user can overwrite/delete
 */
export async function buildBulkTemplate(ledgerNames, companyName = "company") {
  const wb = new ExcelJS.Workbook();
  wb.creator = "AXIORA";
  wb.created = new Date();

  // Hidden reference sheet for ledger dropdown.
  const refSheet = wb.addWorksheet("_Ledgers", { state: "veryHidden" });
  ledgerNames.forEach((n, i) => { refSheet.getCell(i + 1, 1).value = n; });

  const sheet = wb.addWorksheet("Vouchers", { views: [{ state: "frozen", ySplit: 1 }] });
  sheet.columns = [
    { header: TEMPLATE_HEADERS[0], key: "date", width: 14 },
    { header: TEMPLATE_HEADERS[1], key: "debit", width: 28 },
    { header: TEMPLATE_HEADERS[2], key: "credit", width: 28 },
    { header: TEMPLATE_HEADERS[3], key: "narration", width: 36 },
    { header: TEMPLATE_HEADERS[4], key: "amount", width: 14 },
    { header: TEMPLATE_HEADERS[5], key: "vtype", width: 14 },
  ];

  // Header styling
  const header = sheet.getRow(1);
  header.font = { bold: true, color: { argb: "FFFFFFFF" } };
  header.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1D4ED8" } };
  header.alignment = { vertical: "middle", horizontal: "left" };
  header.height = 22;
  header.eachCell((cell) => {
    cell.border = {
      top: { style: "thin", color: { argb: "FF1E3A8A" } },
      left: { style: "thin", color: { argb: "FF1E3A8A" } },
      bottom: { style: "thin", color: { argb: "FF1E3A8A" } },
      right: { style: "thin", color: { argb: "FF1E3A8A" } },
    };
    cell.protection = { locked: true };
  });

  const MAX_ROWS = 500;
  const lastRow = MAX_ROWS + 1;
  const ledgerListRef = ledgerNames.length
    ? `=_Ledgers!$A$1:$A$${ledgerNames.length}`
    : '"(no ledgers)"';
  const vtypeListRef = `"${VOUCHER_TYPES.join(",")}"`;

  for (let r = 2; r <= lastRow; r++) {
    sheet.getCell(`A${r}`).dataValidation = {
      type: "date",
      operator: "greaterThan",
      formulae: [new Date(1900, 0, 1)],
      allowBlank: true,
      showErrorMessage: true,
      errorTitle: "Invalid date",
      error: "Please enter date in dd/mm/yyyy format.",
      showInputMessage: true,
      promptTitle: "Date",
      prompt: "Enter date in dd/mm/yyyy format.",
    };
    sheet.getCell(`A${r}`).numFmt = "dd/mm/yyyy";

    sheet.getCell(`B${r}`).dataValidation = {
      type: "list", allowBlank: true, formulae: [ledgerListRef],
      showErrorMessage: true, errorTitle: "Invalid ledger",
      error: "Invalid data has been entered. Select a ledger from the dropdown.",
    };
    sheet.getCell(`C${r}`).dataValidation = {
      type: "list", allowBlank: true, formulae: [ledgerListRef],
      showErrorMessage: true, errorTitle: "Invalid ledger",
      error: "Invalid data has been entered. Select a ledger from the dropdown.",
    };

    sheet.getCell(`D${r}`).numFmt = "@";

    sheet.getCell(`E${r}`).dataValidation = {
      type: "decimal", operator: "greaterThanOrEqual", formulae: [0],
      allowBlank: true, showErrorMessage: true,
      errorTitle: "Invalid amount",
      error: "Only numbers can be entered in this column.",
    };
    sheet.getCell(`E${r}`).numFmt = "#,##0.00";

    sheet.getCell(`F${r}`).dataValidation = {
      type: "list", allowBlank: true, formulae: [vtypeListRef],
      showErrorMessage: true, errorTitle: "Invalid voucher type",
      error: "Pick one of: Journal, Payment, Receipt, Contra.",
      showInputMessage: true,
      promptTitle: "Voucher Type",
      prompt: "Default is Journal. Choose Payment / Receipt / Contra to override.",
    };
  }

  // Unlock the editable region
  for (let r = 2; r <= lastRow; r++) {
    ["A", "B", "C", "D", "E", "F"].forEach((col) => {
      sheet.getCell(`${col}${r}`).protection = { locked: false };
    });
  }

  // Two sample rows (italic + dimmer color so they're visibly examples)
  if (ledgerNames.length >= 2) {
    const sample = (r, vals) => {
      vals.forEach((v, i) => {
        const cell = sheet.getCell(r, i + 1);
        cell.value = v;
        cell.font = { italic: true, color: { argb: "FF94A3B8" } };
      });
    };
    const today = new Date();
    const yesterday = new Date(Date.now() - 86400000);
    sample(2, [today, ledgerNames[0], ledgerNames[1] || ledgerNames[0], "Example entry — overwrite or delete", 1000, "Journal"]);
    sample(3, [yesterday, ledgerNames[1] || ledgerNames[0], ledgerNames[0], "Another example", 2500.5, "Payment"]);
  }

  sheet.properties.defaultRowHeight = 18;
  await sheet.protect("", {
    selectLockedCells: false, selectUnlockedCells: true,
    formatCells: false, formatColumns: false, formatRows: false,
    insertRows: false, insertColumns: false,
    deleteRows: false, deleteColumns: false,
    sort: false, autoFilter: false, pivotTables: false,
  });

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const safeName = String(companyName).replace(/[^a-z0-9-]+/gi, "-").toLowerCase() || "company";
  triggerDownload(blob, `axiora-bulk-vouchers-${safeName}.xlsx`);
}

// Excel serial date → ISO
const excelSerialToISO = (n) => new Date(Math.round((n - 25569) * 86400 * 1000)).toISOString().slice(0, 10);

const parseDateCell = (v) => {
  if (v == null || v === "") return "";
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  if (typeof v === "number") return excelSerialToISO(v);
  const s = String(v).trim().split(" ")[0];
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return s;
  const dmy = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/.exec(s);
  if (dmy) {
    const [, d, m, y] = dmy;
    return `${y}-${m.padStart(2, "0")}-${d.padStart(2, "0")}`;
  }
  return s;
};

const SAMPLE_NARRATIONS = new Set(["Example entry — overwrite or delete", "Another example"]);

/** Parse an uploaded template into rows ready for /vouchers/bulk. */
export async function parseBulkFile(file) {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: "array", cellDates: true });
  const sheet = wb.Sheets["Vouchers"] || wb.Sheets[wb.SheetNames[0]];
  if (!sheet) throw new Error("No sheet found in the uploaded file");
  const aoa = XLSX.utils.sheet_to_json(sheet, { header: 1, raw: true, defval: "" });
  if (!aoa.length) return [];

  const rows = [];
  for (let i = 1; i < aoa.length; i++) {
    const r = aoa[i];
    if (!r || r.every((c) => c === "" || c == null)) continue;
    const [date, debit, credit, narration, amount, vtype] = r;
    const narr = String(narration || "");
    // Skip the demonstration sample rows automatically
    if (SAMPLE_NARRATIONS.has(narr.trim())) continue;
    rows.push({
      excel_row: i + 1,
      date: parseDateCell(date),
      debit_ledger: String(debit || "").trim(),
      credit_ledger: String(credit || "").trim(),
      narration: narr,
      amount: typeof amount === "number" ? amount : Number(String(amount).replace(/,/g, "")) || 0,
      voucher_type: String(vtype || "").trim() || "Journal",
    });
  }
  return rows;
}

/** Pre-validate parsed rows on the client before sending to the backend. */
export function validateRowsClient(rows, ledgerNames) {
  const lower = new Set(ledgerNames.map((n) => n.toLowerCase()));
  const types = new Set(VOUCHER_TYPES.map((t) => t.toLowerCase()));
  return rows.map((row) => {
    const issues = [];
    if (!row.date || !/^\d{4}-\d{2}-\d{2}$/.test(row.date)) issues.push("Invalid date");
    if (!row.debit_ledger) issues.push("Missing debit ledger");
    else if (!lower.has(row.debit_ledger.toLowerCase())) issues.push(`Unknown debit ledger '${row.debit_ledger}'`);
    if (!row.credit_ledger) issues.push("Missing credit ledger");
    else if (!lower.has(row.credit_ledger.toLowerCase())) issues.push(`Unknown credit ledger '${row.credit_ledger}'`);
    if (row.debit_ledger && row.credit_ledger && row.debit_ledger.toLowerCase() === row.credit_ledger.toLowerCase())
      issues.push("Dr and Cr cannot be the same ledger");
    if (!(row.amount > 0)) issues.push("Amount must be > 0");
    if (!types.has(String(row.voucher_type).toLowerCase())) issues.push(`Bad voucher type '${row.voucher_type}'`);
    return { ...row, issues };
  });
}

/** Export the failed rows back to Excel for the user to fix and re-upload. */
export async function downloadFailedRows(failed, ledgerNames, companyName) {
  if (!failed.length) return;
  const wb = new ExcelJS.Workbook();
  const sh = wb.addWorksheet("Failed rows");
  sh.columns = [
    { header: "Original Excel row", key: "row", width: 16 },
    { header: "Date", key: "date", width: 12 },
    { header: "Debit Ledger", key: "dr", width: 26 },
    { header: "Credit Ledger", key: "cr", width: 26 },
    { header: "Narration", key: "narration", width: 30 },
    { header: "Amount", key: "amount", width: 14 },
    { header: "Voucher Type", key: "vtype", width: 14 },
    { header: "Error", key: "error", width: 50 },
  ];
  sh.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  sh.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FFB91C1C" } };
  failed.forEach((f) => {
    sh.addRow({
      row: f.row, date: f.date, dr: f.debit_ledger, cr: f.credit_ledger,
      narration: f.narration, amount: f.amount, vtype: f.voucher_type, error: f.error,
    });
  });
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const safe = String(companyName || "company").replace(/[^a-z0-9-]+/gi, "-").toLowerCase();
  triggerDownload(blob, `axiora-bulk-failed-${safe}.xlsx`);
}
